import { useEffect, createContext } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Modal,
  Space,
  Spin,
  Card,
  Row,
  Col,
  Flex,
  Dropdown,
  MenuProps,
} from "antd";

import { DownOutlined } from "@ant-design/icons";

import ObjectForm from "components/Admin/ObjectForm";

import { RootState } from "Redux/store";
import { fetchHabit, updateHabit, createHabit, deleteHabit } from "Redux/Admin/HabitManager/Habits/action";

import { settings, initialSettings, initialValues } from './const';
import { ADMIN_PATH } from "utils/routes";

import type { IHabit } from 'Redux/Admin/HabitManager/Habits/type';

const Habit = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { id } = useParams<{ id: string }>();
  const isNew = id === 'new';

  const { entities, loading, error } = useSelector((state: RootState) => state.fetchHabits);

  const [modal, contextHolder] = Modal.useModal();

  const ReachableContext = createContext<string | null>(null);

  useEffect(() => {
    if (id && !isNew) {
      dispatch(fetchHabit(id));
    }
  }, [dispatch, isNew, id]);

  const onFinish = (values: any) => {
    if (isNew) {
      dispatch(createHabit(values, handleCreate));
    }
    else if (id) {
      dispatch(updateHabit(values, id));
    }
  };

  const handleCreate = (project: IHabit) => {
    if (project && project.id) {
      navigate(`${ADMIN_PATH}/habit-manager/habits/${project.id}`);
    }
  }

  if (!id) {
    navigate(`${ADMIN_PATH}/habit-manager/habits`, { replace: true });
    return null;
  }

  const habit = isNew ? initialValues : entities[id];

  if (!habit) {
    return <Spin />;
  }

  const menuItems: MenuProps['items'] = [
    {
      label: 'Delete Habit',
      key: '2',
      danger: true,
    }
  ];

  const modalConfig = {
    title: 'Delete the habit?',
    content: <span>Are you sure you want to delete the habit <b>{habit.id}</b>?</span>,
    confirmLoading: true
  };

  const handleMenuClick: MenuProps['onClick'] = async (e) => {
    if (e.key === '2') {
      const confirmed = await modal.confirm(modalConfig);

      if (confirmed) {
        dispatch(deleteHabit(id, () => {
          navigate(`${ADMIN_PATH}/habit-manager/habits`, { replace: true });
        }));
      }
    }
  };

  const menuProps = {
    items: menuItems,
    onClick: handleMenuClick,
  };

  return (
    <ReachableContext.Provider value="Light">
      <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Row>
          <Col span={12}>
            <Flex
              gap="middle"
              align="center"
              justify="space-between"
              style={{ marginBottom: 16 }}
            >
              <Link to={`${ADMIN_PATH}/habit-manager/habits`}>Back</Link>
            </Flex>

            <Card title={`${isNew ? 'Create' : '' } Habit`} extra={
              isNew ? null : <Dropdown menu={menuProps} placement="bottom">
                <Button>
                  <Space>
                    Manage Habit
                    <DownOutlined />
                  </Space>
                </Button>
              </Dropdown>
            }>
              <ObjectForm
                data={habit}
                errorData={error}
                isNew={isNew}
                settings={isNew ? initialSettings : settings}
                loading={loading}
                onFinish={onFinish}
              />
            </Card>
          </Col>
        </Row>
      </div>

      {contextHolder}
    </ReachableContext.Provider>
  );
}

export default Habit;
